import { omit, path, pick } from 'ramda'
import { gql, useQuery } from '@apollo/client'
import toInteger from 'lodash/toInteger'
import { useSelector } from 'react-redux'

import config from '@/config'
import { getSearch } from '@/utils/location'
import { useFilters } from '@/hooks/use-filters'
import BelmontPropertiesProfile from '@/features/Company/company-profile/belmont-properties'
import belmontPropertiesLogo from '@/features/Company/company-profile/belmont-properties/belmontproperties-logo.png'
import CarreraManagementCorporationProfile from '@/features/Company/company-profile/carrera-management-corporation'
import carreraManagementCorporationLogo from '@/features/Company/company-profile/carrera-management-corporation/carreramanagementcorporationlogo.png'
import CenturionPropertyAssociatesProfile from '@/features/Company/company-profile/centurion-property-associates'
import CenturionPropertyAssociatesLogo from '@/features/Company/company-profile/centurion-property-associates/logo.webp'
import CenturionPropertyAssociatesQr from '@/features/Company/company-profile/centurion-property-associates/qr-code.png'
import ConcertRealtyServicesProfile from '@/features/Company/company-profile/concert-realty'
import ConcertRealtyServicesLogo from '@/features/Company/company-profile/concert-realty/concert-realty-logo.svg'
import GreenwinProfile from '@/features/Company/company-profile/greenwin'
import GreenwinLogo from '@/features/Company/company-profile/greenwin/logo.png'
import HomeLifePropertyManagementProfile from '@/features/Company/company-profile/homelife-property-management'
import HomeLifePropertyManagementLogo from '@/features/Company/company-profile/homelife-property-management/homelife_logo.png'
import InterRentProfile from '@/features/Company/company-profile/interrent'
import InterRentLogo from '@/features/Company/company-profile/interrent/logo.jpg'
import InterRentQr from '@/features/Company/company-profile/interrent/qr-code.png'
import LBrothersProfile from '@/features/Company/company-profile/l-brothers'
import lBrothersLogoBlk from '@/features/Company/company-profile/l-brothers/lbrothers-logo-blk.png'
import lBrothersQr from '@/features/Company/company-profile/l-brothers/lbrothers-qrcode.png'
import MainStreetEquityCorpProfile from '@/features/Company/company-profile/mainstreet-equity-corp'
import MainStreetEquityCorpLogo from '@/features/Company/company-profile/mainstreet-equity-corp/logo.jpg'
import MainStreetEquityCorpQr from '@/features/Company/company-profile/mainstreet-equity-corp/qr-code.png'
import MetcapLivingProfile from '@/features/Company/company-profile/metcap-living'
import MetcapLivingLogo from '@/features/Company/company-profile/metcap-living/logo.png'
import MetcapLivingQr from '@/features/Company/company-profile/metcap-living/qr-code.png'
import promptonProfile from '@/features/Company/company-profile/prompton'
import promptonLogo from '@/features/Company/company-profile/prompton/prompton-logo.png'
import promptonQr from '@/features/Company/company-profile/prompton/prompton-qrcode.png'
import RealstarProfile from '@/features/Company/company-profile/realstar'
import RealstarLogo from '@/features/Company/company-profile/realstar/logo.webp'
import SterlingKaramarProfile from '@/features/Company/company-profile/sterling-karamar'
import SterlingKaramarLogo from '@/features/Company/company-profile/sterling-karamar/sterling-karamar-logo.png'
import SterlingKaramarQR from '@/features/Company/company-profile/sterling-karamar/sterling-karamar-qr-code.png'
import UooiPropertyManagementProfile from '@/features/Company/company-profile/uooi-property-management'
import uooiPropertyManagementLogo from '@/features/Company/company-profile/uooi-property-management/uooipropertymanagement-logo.png'
import VancouverPropertyManagementProfile from '@/features/Company/company-profile/vancouver-property-management'
import RemaxPropertyManagementLogo from '@/features/Company/company-profile/vancouver-property-management/remax-crest-realty.jpeg'
import VancouverPropertyManagementLogo from '@/features/Company/company-profile/vancouver-property-management/vanpmmaster_logo.png'
import VantageWestRealtyProfile from '@/features/Company/company-profile/vantage-west-realty'
import VantageWestRealtyLogo from '@/features/Company/company-profile/vantage-west-realty/vantage-west-realty-logo.png'
import WBrothersProfile from '@/features/Company/company-profile/w-brothers'
import wBrothersLogo from '@/features/Company/company-profile/w-brothers/wbrothers-logo.png'
import { GatherColivingSeo } from '@/features/Company/seo'

const isDev = config.BUILD_ENV === 'dev'

const PROFILES = {
  lbrothers: {
    id: isDev ? 100000725 : 25332,
    pageType: 'vip',
    url: 'lbrothers',
    qr: lBrothersQr,
    qrAlt: 'L Brothers QR Code',
    logoAlt: 'L Brothers Real Estate Team',
    logo: lBrothersLogoBlk,
    profile: LBrothersProfile
  },
  prompton: {
    id: isDev ? 168 : 10,
    pageType: 'vip',
    url: 'prompton',
    qr: promptonQr,
    qrAlt: 'Prompton QR Code',
    logoAlt: 'Prompton Real Estate Inc.',
    logo: promptonLogo,
    profile: promptonProfile
  },
  wbrothers: {
    id: isDev ? 235 : 817,
    pageType: 'vip',
    url: 'wbrothers',
    name: 'Kevin Wang Team',
    logoAlt: 'W Brothers Real Estate Team',
    logo: wBrothersLogo,
    profile: WBrothersProfile
  },
  uooipropertymanagement: {
    id: isDev ? 1 : 394,
    pageType: 'vip',
    url: 'uooipropertymanagement',
    logoAlt: 'Uooi Property Management Company',
    logo: uooiPropertyManagementLogo,
    profile: UooiPropertyManagementProfile
  },
  carreramanagementcorporation: {
    id: isDev ? 3 : 163261,
    pageType: 'vip',
    url: 'carreramanagementcorporation',
    logoAlt: 'Carrera Management Corporation',
    logo: carreraManagementCorporationLogo,
    profile: CarreraManagementCorporationProfile
  },
  belmontproperties: {
    id: isDev ? 17 : 148513,
    pageType: 'vip',
    url: 'belmontproperties',
    logoAlt: 'Belmont Properties',
    logo: belmontPropertiesLogo,
    profile: BelmontPropertiesProfile
  },
  gathercoliving: {
    id: isDev ? 14 : 28356,
    pageType: 'custom',
    url: 'gathercoliving',
    name: 'Gather Co-living',
    seoTitle: `Find private rooms for rent in Vancouver offered by Gather Coliving - Vancouver's #1 coliving operator. Search rental listings currently available on liv.rent`,
    seoImageAlt: 'gather coliving rooms for rent vancouver',
    seoComponent: GatherColivingSeo
  },
  sterlingkaramar: {
    id: isDev ? 1 : 46467,
    pageType: 'vip',
    url: 'sterlingkaramar',
    siteUrl: 'https://www.sterlingkaramar.com',
    customSize: {
      height: '100px',
      width: '200px',
      mobileHeight: '75px',
      mobileWidth: '150px'
    },
    name: 'Sterling Karamar Property Management',
    logoAlt: 'Sterling Karamar Property Management Logo',
    logo: SterlingKaramarLogo,
    qr: SterlingKaramarQR,
    profile: SterlingKaramarProfile
  },
  concertrealtyservices: {
    id: isDev ? 1 : 11286,
    pageType: 'vip',
    url: 'concertrealtyservices',
    siteUrl: 'https://www.concertproperties.com/rentals',
    customSize: {
      height: '300px',
      width: '275px',
      mobileHeight: '175px',
      mobileWidth: '150px'
    },
    name: 'Concert Realty Services',
    logoAlt: 'Concert Realty Services Logo',
    logo: ConcertRealtyServicesLogo,
    profile: ConcertRealtyServicesProfile
  },
  vancouverpropertymanagementmaster: {
    id: isDev ? 1 : 126786,
    pageType: 'vip',
    url: 'vancouverpropertymanagementmaster',
    customSize: {
      height: '52px',
      width: '130px',
      mobileHeight: '30px',
      mobileWidth: '75px'
    },
    name: 'Vancouver Property Management',
    logoAlt: 'Vancouver Property Management Logo',
    logo: VancouverPropertyManagementLogo,
    secondaryLogo: RemaxPropertyManagementLogo,
    profile: VancouverPropertyManagementProfile
  },
  vantagewestrealty: {
    id: isDev ? 1 : 190858,
    pageType: 'vip',
    url: 'vantagewestrealty',
    siteUrl: 'https://www.propertymanagementkelowna.com/',
    customSize: {
      height: '60px',
      width: '226px',
      mobileHeight: '40px',
      mobileWidth: '150px'
    },
    name: 'Vantage West Realty',
    logoAlt: 'Vantage West Realty Logo',
    logo: VantageWestRealtyLogo,
    profile: VantageWestRealtyProfile
  },
  emmypang: {
    id: isDev ? 100005902 : 17757,
    pageType: 'custom',
    url: 'emmypang',
    name: 'Emmy Pang ABC Realty'
  },
  firststaybc: {
    id: isDev ? 100005900 : 928,
    pageType: 'custom',
    url: 'firststaybc',
    name: 'First Stay BC'
  },
  carolynchen: {
    id: isDev ? 168 : 6263,
    pageType: 'custom',
    url: 'carolynchen',
    name: 'Carolyn Chen'
  },
  ramongutierrez: {
    id: isDev ? 168 : 1221,
    pageType: 'custom',
    url: 'ramongutierrez',
    name: 'Ramon Guiterrez'
  },
  metcapliving: {
    id: isDev ? 100009917 : 9859,
    pageType: 'vip',
    url: 'metcapliving',
    siteUrl: 'https://www.metcap.com/',
    qr: MetcapLivingQr,
    qrAlt: 'MetCap Living QR Code',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'Metcap Living',
    logoAlt: 'Metcap Living Logo',
    logo: MetcapLivingLogo,
    profile: MetcapLivingProfile
  },
  mainstreetequitycorp: {
    id: isDev ? 100010439 : 238826,
    pageType: 'vip',
    url: 'mainstreetequitycorp',
    siteUrl: 'https://www.mainst.biz/',
    qr: MainStreetEquityCorpQr,
    qrAlt: 'Main Street Equity Corp. QR Code',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'Main Street Equity Corp.',
    logoAlt: 'Main Street Equity Corp. Logo',
    logo: MainStreetEquityCorpLogo,
    profile: MainStreetEquityCorpProfile
  },
  centurionpropertyassociates: {
    id: isDev ? 100010661 : 239564,
    pageType: 'vip',
    url: 'centurionpropertyassociates',
    siteUrl: 'https://www.cpliving.com/',
    qr: CenturionPropertyAssociatesQr,
    qrAlt: 'Centurion Property Associates Inc. QR Code',
    customSize: {
      height: '100px',
      width: '100px',
      mobileHeight: '55px',
      mobileWidth: '55px'
    },
    name: 'Centurion Property Associates Inc.',
    logoAlt: 'Centurion Property Associates Inc. Logo',
    logo: CenturionPropertyAssociatesLogo,
    profile: CenturionPropertyAssociatesProfile
  },
  homelifepropertymanagementbc: {
    id: isDev ? 1 : 94737,
    pageType: 'vip',
    url: 'homelifepropertymanagementbc',
    siteUrl: 'https://homelifepropertyrentals.ca/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'HomeLife Property Management',
    logoAlt: 'HomeLife Property Management Logo',
    logo: HomeLifePropertyManagementLogo,
    profile: HomeLifePropertyManagementProfile
  },
  interrent: {
    id: isDev ? 100010723 : 240525,
    pageType: 'vip',
    url: 'interrent',
    siteUrl: 'https://www.irent.com/en/',
    qr: InterRentQr,
    qrAlt: 'InterRent QR Code',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'InterRent',
    logoAlt: 'InterRent Logo',
    logo: InterRentLogo,
    profile: InterRentProfile
  },
  greenwin: {
    id: isDev ? 100009917 : 247067,
    pageType: 'vip',
    url: 'greenwin',
    siteUrl: 'https://www.greenwin.ca/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    logo: GreenwinLogo,
    logoAlt: 'Greenwin Logo',
    profile: GreenwinProfile
  },
  realstarcorp: {
    id: isDev ? 100010723 : 221185,
    pageType: 'vip',
    url: 'realstarcorp',
    siteUrl: 'https://www.realstar.ca/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'Realstar Corp',
    logoAlt: 'Realstar Logo',
    logo: RealstarLogo,
    profile: RealstarProfile
  }
}

export const COMPANY_DATA_QUERY = gql`
  fragment brokerInfo on FullListing {
    broker {
      name
    }
    broker_files {
      tag
      location {
        key
      }
    }
  }

  fragment companyListViewFields on FullListing {
    street_name
    building_type
    building_name
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_price
    gr_max_price
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    verified_state_machine
    is_hidden
    state_machine
    landlords {
      identity_verified_state
    }
    allow_applications
    public_viewing
    unit_files {
      aws_s3_key
      position
      tag
    }
  }
  query company($input: CompanyListSearchInput!, $featInput: ListSearchInput!) {
    companyListSearch {
      companyListings(input: $input) {
        metadata {
          page_size
          page
          total_count
        }
        feed {
          ...companyListViewFields
          ...brokerInfo
        }
      }
      featured(input: $featInput) {
        ...companyListViewFields
      }
    }
  }
`

export function useCompanyData(companyId) {
  return useSelector(path(['company', companyId]))
}

export function useCompanyProfile(companyId) {
  return PROFILES[companyId]
}

export function getCompanyProfileById(companyId) {
  const profiles = Object.values(PROFILES)
  const profile = profiles.find(
    ({ id, url }) => `${id}` === companyId || url === companyId
  )
  return profile
}

const getFeaturedFilter = filters => {
  const standard = { featured: 3, cities: [] }
  const user = pick(
    [
      'page',
      'page_size',
      'housing_types',
      'unit_types',
      'bedroom_count',
      'furnished'
    ],
    filters
  )
  return { ...standard, ...user }
}

export const getVariables = filters => {
  return {
    input: omit(['cities', 'featured'], filters),
    featInput: getFeaturedFilter(filters)
  }
}

export function useCompanyListingsData(companyId) {
  const [originalFilters] = useFilters()

  const filters = {
    state_machine: 'published',
    company_id: toInteger(companyId),
    ...originalFilters,
    cities: null
  }

  const {
    data = {},
    error,
    client,
    loading
  } = useQuery(COMPANY_DATA_QUERY, {
    variables: getVariables(filters)
  })

  const preload = newFilter => {
    client.query({
      query: COMPANY_DATA_QUERY,
      variables: getVariables({ ...filters, ...newFilter })
    })
  }

  return { data, error, loading, preload, filters }
}

export async function getCompanyData({
  req,
  query,
  params,
  reduxStore,
  apolloClient
}) {
  const { dispatch } = reduxStore

  const id = params?.id
  const companyProfile = PROFILES[id]
  const companyId = companyProfile?.id || id
  await dispatch.company.load(companyId, req)
  const search = getSearch({ query }, { setDefaultCity: false })
  dispatch.listings.updateCompanyFilter({
    sort: { sort_by: 'CREATED_AT', sort_type: 'DESC' },
    ...search,
    state_machine: 'published',
    company_id: companyId,
    cities: null
  })

  apolloClient.query({
    query: COMPANY_DATA_QUERY,
    variables: getVariables(reduxStore.getState().listings.filters)
  })
}
